import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ArianeNavComponent } from 'src/app/layout/ariane-nav/ariane-nav.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { PagedResultDTO } from '@models/shared';
import { BehaviorSubject, map, Observable, of, Subject, switchMap, take } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { AgentsService } from 'src/app/core/services/agents.service';
import { FinContratFilterParams } from '@models/api/requests';
import { DateTime } from 'luxon';
import { ReferentielsService } from 'src/app/core/services/referentiels.service';
import { environment } from 'src/environments/environment';
import { DataTableOptimizedComponent } from 'src/app/shared/data-table-optimized/data-table-optimized.component';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { CustomLuxonDateAdapter } from 'src/app/shared/utils/custom-luxon-date-adapter';
import { MY_FORMATS } from 'src/app/shared/utils/date-pickr-custom-luxon-format';
import { Router } from '@angular/router';
import { ExcelService } from 'src/app/core/services/excel.service';
import { FinContratFilterComponent } from './fin-contrat-filter/fin-contrat-filter.component';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { PdfViewerComponent } from 'src/app/shared/pdf-viewer/pdf-viewer.component';
import { DemandeType } from '@models/enums';

@Component({
    selector: 'app-consultation-fin-contrat',
    imports: [
        CommonModule,
        ArianeNavComponent,
        MatDividerModule,
        MatIconModule,
        FinContratFilterComponent,
        MatExpansionModule,
        MatInputModule,
        MatButtonModule,
        DataTableOptimizedComponent,
        MatDialogModule
    ],
    templateUrl: './consultation-fin-contrat.component.html',
    styleUrls: ['./consultation-fin-contrat.component.scss'],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
        { provide: DateAdapter, useClass: CustomLuxonDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
        {
            provide: MAT_RADIO_DEFAULT_OPTIONS,
            useValue: { color: 'primary' }
        }
    ]
})
export class ConsultationFinContratComponent implements OnInit, OnDestroy {
  arianeLabel: string = 'Consultation fin de contrats';
  arianeIcon: string = 'quick_reference_all';
  arianeList = [
    {
      label: 'Consultation fin de contrats',
      url: '/pama/consultation-fin-contrats'
    }
  ];
  panelOpenState: boolean = false;
  filterSubject = new BehaviorSubject<FinContratFilterParams | null>({
    agent: null,
    fonction: null,
    structureHierarchique: null,
    dateRetourDirection: null,
    dateDebutContrat: null,
    dateFinContrat: null,
    infoService: null,
    dateEntreeOrganisation: null,
    posteBudgete: null,
    typeCT: null,
    codeEngagement: null,
    infoPoste: null,
    agentRemplace: null,
    dateExpirationCarteSejour: null,
    categoriePoste: null,
    grade: null,
    tauxSitAdmin: null,
    statut: null
  });
  activeFilterCount$: Observable<number>;
  typeContratList: any[] = [];

  demandesDisplayedColumns: string[] = [
    'Agent',
    'Structure hiérarchique',
    'Fonction',
    'Dernier retour dir (Maxi FC)',
    'Début-fin contrat',
    'Info service',
    'Date entrée organisation',
    'Poste budgété 2025',
    'Type CT',
    'Code engagement',
    'Info poste',
    'Agt remplacé',
    'Date expiration carte séjour',
    'Catégorie poste',
    'Grade',
    'Taux sit.admin',
    'Statut',
    'Actions'
  ];

  demandesCellStyleCss: any = {
    Statut: { 'text-align': 'center' },
    'Date création': { 'text-align': 'center' }
  };

  /**
   * Formats an ISO or yyyy-MM-dd date string to dd/MM/yyyy.
   * Returns an empty string for null/undefined/empty inputs or invalid date formats.
   * @param date The date string to format.
   * @returns Formatted date string ('dd/MM/yyyy') or an empty string.
   */
  formatDate(date: string | null | undefined): string {
    if (!date) {
      return ''; // Return empty string for falsy input
    }

    let luxonDate = DateTime.fromISO(date);

    if (!luxonDate.isValid) {
      // If ISO fails, try parsing as yyyy-MM-dd
      luxonDate = DateTime.fromFormat(date, 'yyyy-MM-dd');
    }

    if (!luxonDate.isValid) {
      return ''; // Return empty string if both parsing attempts fail
    }

    return luxonDate.toFormat('dd/MM/yyyy'); // Format valid date
  }

  
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly agentService: AgentsService,
    private readonly referentielsService: ReferentielsService,
    private readonly router: Router,
    private readonly excelService: ExcelService,
    public dialog: MatDialog
  ) {
    this.activeFilterCount$ = this.filterSubject.pipe(map((filter) => Object.entries(filter ?? {}).filter(([_, value]) => value).length));
  }
  ngOnInit() {
    this.loadListTypeContrat();
  }

  togglePanel() {
    this.panelOpenState = !this.panelOpenState;
  }

  onFilterApplied(formValues: any) {
    const filterParams: FinContratFilterParams = {
      agent: formValues.agent?.id ?? null,
      fonction: formValues.fonction ?? null,
      structureHierarchique: formValues.structureHierarchique ?? null,
      dateRetourDirection: formValues.dateRetourDirection ?? null,
      dateDebutContrat: formValues.dateDebutContrat ?? null,
      dateFinContrat: formValues.dateFinContrat ?? null,
      infoService: formValues.infoService?.libelle ?? null,
      dateEntreeOrganisation: formValues.dateEntreeOrganisation ?? null,
      posteBudgete: formValues.posteBudgete !== '' ? formValues.posteBudgete : null,
      typeCT: formValues.typeCT?.id ?? null,
      codeEngagement: formValues.codeEngagement ?? null,
      infoPoste: formValues.infoPoste ? `${formValues.infoPoste.code} - ${formValues.infoPoste.libelle}` : null,
      agentRemplace:
        formValues.agentRemplace?.nom && formValues.agentRemplace?.prenom
          ? `${formValues.agentRemplace.nom} ${formValues.agentRemplace.prenom}`
          : null,
      dateExpirationCarteSejour: formValues.dateExpirationCarteSejour ?? null,
      categoriePoste: formValues.categoriePoste?.id ?? null,
      grade: formValues.grade ?? null,
      tauxSitAdmin: formValues.tauxSitAdmin ?? null,
      statut: formValues.statut ?? null
    };

    this.filterSubject.next(filterParams);
  }

  getAgentsList = (pageIndex: number, pageSize: number, filter?: FinContratFilterParams): Observable<PagedResultDTO<any>> => {
    return this.agentService.getAllAgentWithParams(pageIndex, pageSize, filter).pipe(
      map((response) => {
        return {
          ...response,
          results: this.transformDataListeAgent(response.results)
        };
      })
    );
  };

  loadListTypeContrat() {
    this.referentielsService
      .getTypeContrat()
      .pipe(take(1))
      .subscribe((data: any[]) => {
        if (data) {
          this.typeContratList = data.filter((o) => o.libelle && o.libelle.trim() !== '');
        }
      });
  }

  transformDataListeAgent(finContrat: any[]): any {
    return finContrat.map((agent) => {
      return {
        id: agent?.id,
        Agent: `${agent?.nom || ''} ${agent?.prenom || ''}`.trim(),
        Fonction: agent?.fonction,
        Grade: agent['grade'],
        'Agt remplacé': agent['agentRemplaceNom'],
        'Type CT': this.getTypeContratLabel(agent['typeContrat']),
        'Date entrée organisation': this.formatDate(agent['dateEntree']),
        'Début-fin contrat': `${agent?.dateDebutContrat ? this.formatDate(agent.dateDebutContrat) : ''} - ${agent?.dateFinContrat ? this.formatDate(agent.dateFinContrat) : ''}`,
        'Info service': agent.serviceFonctionnel?.libelle ?? null,
        'Info poste': agent?.posteOccupeObj ? `${agent.posteOccupeObj.code} - ${agent.posteOccupeObj.libelle}` : null,
        'Catégorie poste': agent.categoriePosteObj?.code ?? null,
        'Structure hiérarchique': agent.libelleStructure ?? null,
        'Taux sit.admin': agent.tauxEmploi ?? null,
        'Code engagement': agent.codeEngagement ?? null,
        'Date expiration carte séjour': agent.dateExpirationCS ? this.formatDate(agent.dateExpirationCS) : null,
        'Poste budgété 2025': agent.posteOccupeObj?.budgete ? 'Oui' : 'Non',
        'Dernier retour dir (Maxi FC)': agent.dateRetourDirection ? this.formatDate(agent.dateRetourDirection) : '',
        'Statut': agent?.flagDemandeEnCours 
          ? { class: 'status-validated-n1', text: 'Renouvellement en cours' }
          : { class: 'status-pending', text: 'A renouveler' },
        renew_hidden: agent?.flagDemandeEnCours === true,
        // Add consultation action for agents with ongoing renewal
        view_hidden: !agent?.flagDemandeEnCours,
        view_disabled: false,
        // Include the renewal request ID for consultation
        idDemandeEnCours: agent?.idDemandeEnCours
      };
    });
  }

  transformDataListeAgentForExport(finContrat: any[]): any {
    return finContrat.map((agent) => {
      return {
        Agent: `${agent?.nom || ''} ${agent?.prenom || ''}`.trim(),
        'Structure hiérarchique': agent.libelleStructure ?? null,
        Fonction: agent?.fonction,
        'Dernier retour dir (Maxi FC)': agent.dateRetourDirection ? this.formatDate(agent.dateRetourDirection) : '',
        'Début-fin contrat': `${agent?.dateDebutContrat ? this.formatDate(agent.dateDebutContrat) : ''} - ${agent?.dateFinContrat ? this.formatDate(agent.dateFinContrat) : ''}`,
        'Info service': agent.serviceFonctionnel?.libelle ?? null,
        'Date entrée organisation': this.formatDate(agent['dateEntree']),
        'Poste budgété 2025': agent.posteOccupeObj?.budgete ? 'Oui' : 'Non',
        'Type CT': this.getTypeContratLabel(agent['typeContrat']),
        'Code engagement': agent.codeEngagement ?? null,
        'Info poste': agent?.posteOccupeObj ? `${agent.posteOccupeObj.code} - ${agent.posteOccupeObj.libelle}` : null,
        'Agt remplacé': agent['agentRemplaceNom'],
        'Date expiration carte séjour': agent.dateExpirationCS ? this.formatDate(agent.dateExpirationCS) : null,
        'Catégorie poste': agent.categoriePosteObj?.code ?? null,
        Grade: agent['grade'],
        'Taux sit.admin': agent.tauxEmploi ?? null,
        'Statut': agent?.flagDemandeEnCours ? 'Renouvellement en cours' : 'A renouveler'
      };
    });
  }

  getTypeContratLabel(id: number): string {
    const typeContrat = this.typeContratList.find((t) => t.id === id);
    return typeContrat ? typeContrat.libelle : '';
  }
  renouvelerContrat(event: any) {
    if (event?.id) {
      this.router.navigate(['/pama/renouvellement'], { queryParams: { agentId: event.id } });
    }
  }
  exportFinContrat() {
    const currentFilters = this.filterSubject.value;

    this.agentService
      .getAllAgentWithParams(0, environment.pagination.limit, currentFilters)
      .pipe(
        take(1),
        switchMap((firstPage) => {
          if (!firstPage) {
            return of([]);
          }

          const firstPageResults = firstPage.results ?? [];
          const totalRecords = firstPage.totalRecords ?? firstPageResults.length;

          if (totalRecords <= firstPageResults.length) {
            return of(firstPageResults);
          }

          return this.agentService
            .getAllAgentWithParams(0, totalRecords, currentFilters)
            .pipe(map((fullResult) => fullResult?.results ?? []));
        }),
        map((allResults) => this.transformDataListeAgentForExport(allResults))
      )
      .subscribe((exportData) => {
        this.convertExcel(exportData, 'export_données_consultation_fin_contrat');
      });
  }
  convertExcel(data: any, name: string) {
    const search = '/';
    const replaceWith = '-';
    const date = new Date().toLocaleDateString('en-GB').split(search).join(replaceWith);
    let fileName = `${name}_${date}`;
    this.excelService.exportToExcel(data, `${fileName}.xlsx`, 'Sheet1');
  }

  /**
   * View renewal request details for agents with "Renouvellement en cours" status
   * @param agent Agent data from the table
   */
  viewRenewalDetails(agent: any) {
    // Use the idDemandeEnCours directly from agent data
    if (!agent.idDemandeEnCours) {
      console.warn('Aucun ID de demande de renouvellement trouvé pour cet agent');
      return;
    }

    this.dialog.open(PdfViewerComponent, {
      width: '2000px',
      height: '100%',
      maxWidth: '100vw',
      panelClass: 'dialog-bg-trans',
      data: {
        demandes: [{ id: agent.idDemandeEnCours, type: DemandeType.Renouvellement }],
        filename: `demande-${agent.idDemandeEnCours}`,
        action: 'view'
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
